import analytics from './analytics-unified';
import { isDevelopmentOrTest } from '../helpers/environment_helper';
import { logger as log } from '../helpers/logging_helper';

export interface Variant {
  id: string;
  text: string;
}

interface Test {
  variants: Variant[];
  weights: number[];
}

interface TestAssignments {
  [testId: string]: number;
}

const STORAGE_KEY = 'fig_ab_tests';

export const TEST_IDS = {
  SMART_FINANCIAL_CTA: 'smart_financial_cta',
  PREQUAL_DISCLOSURE_PLACEMENT: 'prequal_disclosure_placement'
} as const;

export type TestId = typeof TEST_IDS[keyof typeof TEST_IDS];

export const TESTS: Record<TestId, Test> = {
  [TEST_IDS.SMART_FINANCIAL_CTA]: {
    variants: [
      { id: 'A', text: 'Make a smart financial decision today!' },
      { id: 'B', text: 'Make a smart financial decision today' }, // No exclamation point
    ],
    weights: [0.5, 0.5]
  },
  [TEST_IDS.PREQUAL_DISCLOSURE_PLACEMENT]: {
    variants: [
      { id: 'A', text: 'Control - current state' },
      { id: 'B', text: 'Variant - AFS consent on Disclosures page, CTA removed from bank details' }
    ],
    weights: [0.5, 0.5]
  }
};



// Check for URL parameter override in development/test environments
function getUrlOverride(testId: TestId, test: Test): Variant | null {
  if (!isDevelopmentOrTest()) return null;
  
  const params = new URLSearchParams(window.location.search);
  const overrideKey = `variant_${testId}`;
  const override = params.get(overrideKey);
  
  if (!override) return null;
  
  return test.variants.find(v => v.id === override) || null;
}

// Read test assignments from localStorage
function readAssignments(): TestAssignments {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    log.warn(`Failed to parse A/B test assignments from localStorage: ${error}`);
    // Clear data and start fresh
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (removeError) {
      log.warn(`Failed to clear corrupted localStorage: ${removeError}`);
    }
    return {};
  }
}

// Save test assignments to localStorage
function saveAssignments(assignments: TestAssignments): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
  } catch (error) {
    log.warn(`Failed to store A/B test assignment: ${error}`);
  }
}

// Assign a variant using weighted random selection with normalization
function assignVariantByWeight(test: Test, randomNumber: number): Variant {
  // Normalize the random number to the scale of the weights
  const totalWeight = test.weights.reduce((sum, weight) => sum + weight, 0);
  const normalizedRand = randomNumber * totalWeight;

  let refWeight = 0;
  for (let i = 0; i < test.variants.length; i++) {
    refWeight += test.weights[i];
    if (normalizedRand <= refWeight) {
      return test.variants[i];
    }
  }

  // This should never be reached with valid inputs, but TypeScript requires a return
  log.warn(`A/B test assignment failed unexpectedly. weights: ${JSON.stringify(test.weights)}, randomNumber: ${randomNumber}, normalizedRand: ${normalizedRand}, totalWeight: ${totalWeight}, variants: ${test.variants.length}`);
  return test.variants[0];
}

// Get or assign variant for a test
export function getTestVariant(testId: TestId): Variant | null {
  const test = TESTS[testId];
  if (!test) return null;

  // Check for URL override (development/test only)
  const urlOverride = getUrlOverride(testId, test);
  if (urlOverride) {
    return urlOverride;
  }

  // Get existing assignments
  const assignments = readAssignments();
  let randomNumber = assignments[testId];
  
  // Generate new random number if none exists
  if (randomNumber === undefined) {
    randomNumber = Math.random();
    assignments[testId] = randomNumber;
    saveAssignments(assignments);
  }

  // Determine variant using the stored random number and current weights
  const variant = assignVariantByWeight(test, randomNumber);
  
  return variant;
}

// Track impression for a test variant
export function trackTestImpression(testId: TestId): void {
  const variant = getTestVariant(testId);
  if (!variant) return;

  const urlOverride = getUrlOverride(testId, TESTS[testId]);
  const isOverride = !!urlOverride;

  analytics.trackExperiment(testId, variant.id, {
    experiment_content: variant.text,
    is_override: isOverride
  });
}